import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { UseToastOptions } from '@chakra-ui/react';
import { useAppToast } from './useAppToast';
import { useQueryClient } from '@tanstack/react-query';
import { FEATURE_FLAGS } from '@bower/shared/constants/featureFlags';
import { gateForErrorCode } from '@bower/shared/billing/gates';

import { useNamespaceSocket } from '../../contexts/SocketContext';
import { invalidateEntityQueries, queryKeys } from '../../lib/queryClient';
import { getWorkspaceId } from '../../utils/workspaceState';
import { ROUTES } from '../../constants/routes';
import { useMobileDetection } from './useMobileDetection';
import { useFeatureFlag } from '../analytics/useFeatureFlag';
import { ProactiveToast } from '../../components/ui/ProactiveToast';
import { emitUpgradeGateBlocked } from '../../services/events/upgradeGateEvents';
import { requestNoteOcrFeedback } from '../../services/events/noteOcrFeedbackEvents';
import { ocrWarningCopy } from '../../utils/ocrWarningCopy';
import { videoInsightsKey } from '../data/useVideoInsights';
import { handleTaskNotification } from './taskNotification';

/**
 * Notification event payload from the backend Socket.IO /notifications namespace.
 */
interface NotificationEvent {
  type: string;
  title: string;
  message: string;
  timestamp: string;
  data?: Record<string, unknown>;
  entityRef?: {
    type: string;
    id: string;
    name?: string;
  };
}

/**
 * Listens for push notifications on the /notifications socket and
 * surfaces them as Chakra UI toasts. Handles QA warnings, job
 * completions, and system notifications.
 *
 * Mount once near the app root (e.g. inside SocketProvider's children).
 */
export function useNotificationSocket(): void {
  const notificationsSocket = useNamespaceSocket('notifications');
  const toast = useAppToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isMobile } = useMobileDetection();
  const liveCallEnabled = useFeatureFlag(FEATURE_FLAGS.LIVE_CALL_PREVIEW_ENABLED);
  const isOnboarding =
    location.pathname === '/onboarding' || location.pathname.startsWith('/onboarding/');

  const navigateToNote = useCallback(
    (noteId: string) => {
      const path = isMobile ? `/m/notes/${noteId}` : `/notes/${noteId}`;
      navigate(path);
    },
    [isMobile, navigate],
  );

  useEffect(() => {
    if (!notificationsSocket) return;

    const handleNotification = (event: NotificationEvent) => {
      // The unified `task:*` pair (spec U-8) first; anything else falls through.
      // Spreadsheet import reports here now (it no longer sends `job:*`).
      if (
        handleTaskNotification(event, {
          toast,
          pathname: location.pathname,
          isMobile,
          isOnboarding,
          navigate,
          getWorkspaceId,
          invalidate: invalidateEntityQueries,
          invalidateVideoInsights: (videoArtifactId) =>
            void queryClient.invalidateQueries({ queryKey: videoInsightsKey(videoArtifactId) }),
          emitUpgradeGate: emitUpgradeGateBlocked,
        })
      ) {
        return;
      }

      // Invalidate equipment query when a job completes or errors for an artifact entity
      if (
        (event.type === 'job:complete' || event.type === 'job:error') &&
        event.entityRef?.type === 'artifact' &&
        event.entityRef.id
      ) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.equipment.detail(event.entityRef.id),
        });
      }

      // A finished/failed video transcode flips the attachment's processingStatus
      // (transcoding→ready/failed). The lists that hide in-flight rows must refresh
      // so the now-playable (or cleared) attachment appears without a manual reload
      // — the sidebar tree, recents, any artifact list, and the note/equipment
      // attachment bar (invalidated below).
      // A finished/failed server-owned note transcription appended content to
      // the NOTE (and flipped the attachment's metadata marker) via updateMany
      // writes that broadcast nothing — this notification is the ONLY refresh
      // signal (see notifications.namespace.ts). entityRef carries the note.
      // `note:ocr-ready` is the background OCR equivalent:
      // the note-ocr job's append and slot writes broadcast nothing either.
      // `upload:ready` is the audio-re-route arm's equivalent (an upload the
      // worker thought was video turned out to be audio-only and flipped
      // transcoding→ready). Same updateMany-broadcasts-nothing constraint, and a
      // browser voice note never enters the upload tray, so the tray's
      // /uploads/:id/status poll is no help either. It is the only signal
      // reaching `artifacts.detail(attachmentId)` — the "Processing this file"
      // surface — since a later `note:transcript-ready` invalidates
      // `detail(noteId)` instead. Refresh-only by design: `mapTypeToStatus` has
      // no case for it, so it raises no toast on top of the "Recording saved"
      // one the recorder already showed at finalize.
      if (ENTITY_REFRESH_TYPES.has(event.type)) {
        // Routed through the shared 150ms-debounced helper rather than firing
        // four broad invalidations inline. It already covers every key this
        // branch used to invalidate by hand — ['hierarchy'], ['recents'],
        // ['attachments'] (the note/equipment attachment bar, which excludes
        // in-flight `transcoding` rows and so only shows a finished transcode
        // once refetched), the per-workspace infinite artifact lists, and the
        // changed artifact's own detail — but narrowly, and batched.
        //
        // Undebounced, this branch was half of the 2026-08-03 refetch storm:
        // five uploads finalising in one 14-minute window fired 20 broad
        // invalidations, each sweeping every mounted artifact query.
        // The upload events carry no workspaceId (see notifications.namespace.ts),
        // and the per-workspace artifact lists worth refreshing are the ones on
        // screen — so scope to the workspace currently in view. Read from the
        // zero-dependency `workspaceState` module rather than WorkspaceContext:
        // that context imports AuthContext, which initialises Firebase at module
        // load, and dragging that chain into this hook broke the suite's IMPORT
        // in CI (auth/invalid-api-key) while passing locally off a real .env key.
        // Reading here also picks up the workspace live at event time.
        const workspaceId = getWorkspaceId();
        invalidateEntityQueries(
          event.entityRef?.type ?? 'attachment',
          'updated',
          event.entityRef?.id ?? '',
          workspaceId ? { workspaceId } : undefined,
        );
      }

      // Quota-driven import and background-OCR failures route to the same
      // upgrade modal the 402 interceptor uses, no toast (the modal carries
      // the message). Backend marks these via `data.errorCode =
      // AI_*_QUOTA_EXHAUSTED`. After the refresh above on purpose: a quota-
      // failed OCR photo stays on the note, and its tile must show that.
      if (emitQuotaGate(event)) return;

      // Background OCR appended a photo's text to a note (noteOcrReady below).
      if (event.type === 'note:ocr-ready') {
        toast({
          ...noteOcrReady(event, location.pathname),
          position: isMobile ? 'bottom' : 'bottom-right',
          variant: 'left-accent',
          isClosable: true,
        });
        return;
      }

      // A teammate started a call, from the mobile app or the web
      // (callStartedToast below). Not on the call page itself: whoever is
      // there is already in it, or about to be.
      if (event.type === 'call:started') {
        if (liveCallEnabled && !isOnboarding && location.pathname !== ROUTES.liveCall) {
          callStartedToast(event, toast, isMobile, () => navigate(ROUTES.liveCall));
        }
        return;
      }

      const status = mapTypeToStatus(event.type);

      // Skip notification types that don't warrant a toast
      if (!status) return;

      const noteRef =
        event.entityRef?.type === 'note' && event.entityRef.id ? event.entityRef : null;

      // Import note created — show success with "View Note" action
      if (event.type === 'import:note-created' && noteRef) {
        // The onboarding Knowledge step and background-task tray already show
        // this progress. Its action would navigate an unfinished account away
        // from the wizard, only for ProtectedRoute to bounce it back.
        if (isOnboarding) return;
        toast({
          position: isMobile ? 'bottom' : 'bottom-right',
          duration: 5000,
          isClosable: true,
          render: ({ onClose }) => (
            <ProactiveToast
              message={event.message}
              actionLabel="View Note"
              onAction={() => navigateToNote(noteRef.id)}
              onClose={onClose}
            />
          ),
        });
        return;
      }

      // Default toast for other notification types
      toast({
        title: event.title,
        description: event.message,
        status,
        position: isMobile ? 'bottom' : 'bottom-right',
        variant: 'left-accent',
        isClosable: true,
        duration: status === 'error' ? 5000 : 3000,
      });
    };

    notificationsSocket.on('notification', handleNotification);
    return () => {
      notificationsSocket.off('notification', handleNotification);
    };
  }, [
    notificationsSocket,
    toast,
    navigate,
    queryClient,
    isMobile,
    navigateToNote,
    isOnboarding,
    location.pathname,
    liveCallEnabled,
  ]);
}

/** Notifications that are the only refresh signal for the entity they name. */
const ENTITY_REFRESH_TYPES = new Set([
  'upload:complete',
  'upload:ready',
  'upload:error',
  'note:transcript-ready',
  'note:transcript-failed',
  'note:ocr-ready',
]);

/**
 * `note:ocr-ready`: the toast says the text landed, with the synchronous
 * path's warning copy when the run had warnings. When the user is on that
 * note's page and the run has a trace, it also asks how accurate the text
 * was, as the synchronous path does; never on another page.
 */
function noteOcrReady(event: NotificationEvent, pathname: string): UseToastOptions {
  const noteId = event.entityRef?.id;
  const traceId = typeof event.data?.traceId === 'string' ? event.data.traceId : null;
  if (noteId && traceId && pathname === ROUTES.note(noteId)) {
    requestNoteOcrFeedback({ noteId, traceId });
  }
  const warningCodes = Array.isArray(event.data?.warningCodes)
    ? event.data.warningCodes.filter((code): code is string => typeof code === 'string')
    : [];
  const warningCopy = ocrWarningCopy(warningCodes);
  return {
    title: 'Text added to the note',
    description: warningCopy,
    status: warningCopy ? 'warning' : 'success',
    duration: warningCopy ? 5000 : 3000,
  };
}

const QUOTA_GATED_FAILURES = new Set(['import:note-failed']);

function emitQuotaGate(event: NotificationEvent): boolean {
  if (!QUOTA_GATED_FAILURES.has(event.type)) return false;

  const errorCode = typeof event.data?.errorCode === 'string' ? event.data.errorCode : null;
  if (!errorCode) return false;

  const gateKey = gateForErrorCode(errorCode);
  if (!gateKey) return false;

  emitUpgradeGateBlocked(gateKey, {});
  return true;
}

function mapTypeToStatus(type: string): 'success' | 'warning' | 'error' | 'info' | undefined {
  switch (type) {
    case 'job:complete':
    case 'upload:complete':
    case 'import:note-created':
    case 'import:attachment-ready':
    case 'note:transcript-ready':
      return 'success';
    case 'job:error':
    case 'upload:error':
    case 'import:note-failed':
    case 'note:transcript-failed':
      return 'error';
    case 'system:info':
      return 'info';
    case 'system:warning':
      return 'warning';
    default:
      return undefined;
  }
}

/**
 * The call-started toast: behind the same per-user flag as the Live call page
 * it opens (the server already leaves the caller's own clients out). It stays
 * until closed, since a call is joinable for as long as it runs, and there is
 * one per caller, so a phone that rejoins stacks nothing.
 */
function callStartedToast(
  event: NotificationEvent,
  toast: ReturnType<typeof useAppToast>,
  isMobile: boolean,
  openCall: () => void,
): void {
  const callerId = typeof event.data?.userId === 'string' ? event.data.userId : 'unknown';
  const id = `call-started:${callerId}`;
  if (toast.isActive(id)) return;
  toast({
    id,
    position: isMobile ? 'bottom' : 'bottom-right',
    duration: null,
    isClosable: true,
    render: ({ onClose }) => (
      <ProactiveToast
        heading={{ icon: 'call', label: event.title }}
        message={event.message}
        actionLabel="Open call"
        onAction={openCall}
        onClose={onClose}
      />
    ),
  });
}
